/**
 * GPU rental landing page — Instagram cold traffic (FR / BE / CH).
 * Short, mobile-first, conversion-focused. Noindex.
 *
 * Compliance: banned words (investissement, investir, rendement garanti, ROI,
 * placer son argent, placement, capital, profiter) are strictly avoided in all copy.
 * Form: prénom + email + téléphone + non-prechecked consent checkbox.
 * Submits to POST /api/insight/leads with funnel="gpu".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Cpu, Handshake, Wallet, Gauge } from "lucide-react";
import { GpuCalculator } from "./GpuCalculator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);
    const prev = document.title;
    document.title = "Loue de la puissance de calcul et reçois des revenus";
    return () => { document.head.removeChild(m); document.title = prev; };
  }, []);
};

const STEPS = [
  { n: "01", t: "Tu choisis ta puissance", d: "Sélectionne la puissance de calcul GPU que tu souhaites mettre à disposition." },
  { n: "02", t: "On organise la location", d: "Nous mettons cette puissance à disposition des entreprises qui en ont besoin. Tu n'as rien à gérer." },
  { n: "03", t: "Tu reçois ta part", d: "Tu perçois ta part des revenus générés par l'utilisation de cette puissance." },
];

const BENEFITS = [
  { icon: Wallet, t: "Revenus potentiels", d: "Des estimations claires basées sur l'utilisation réelle. Non garantis, toujours annoncés en toute transparence." },
  { icon: Handshake, t: "Sans engagement", d: "Tu t'informes librement, sans obligation ni pression commerciale." },
  { icon: Gauge, t: "Clair et transparent", d: "Un processus simple, expliqué étape par étape, sans jargon ni surprise." },
];

const FAQS = [
  {
    q: "Comment sont calculés les revenus ?",
    a: "Ils dépendent de la puissance que tu mets à disposition et de son utilisation réelle. Notre calculateur te donne une estimation indicative avant que tu ne t'engages.",
  },
  {
    q: "Les revenus sont-ils garantis ?",
    a: "Non. Les montants présentés sont des estimations basées sur des hypothèses d'utilisation. Les revenus réels varient selon la demande en puissance de calcul.",
  },
  {
    q: "Dois-je gérer quelque chose moi-même ?",
    a: "Non. Nous mettons la puissance à disposition des entreprises et nous occupons de toute la partie technique. Tu n'as rien à gérer.",
  },
];

export default function GpuLanding() {
  useNoIndex();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.consent) { setErr("Merci de cocher la case de consentement."); return; }
    setBusy(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const source = utm.get("utm_source") || utm.get("source") || "instagram";
      const campaign = utm.get("utm_campaign");
      await axios.post(`${API}/insight/leads`, {
        ...form,
        source: campaign ? `${source}/${campaign}` : source,
        funnel: "gpu",
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.");
    } finally { setBusy(false); }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (window.location.hash === "#calculateur") setTimeout(() => scrollTo("gpu-calculateur"), 300);
  }, []);

  return (
    <div className="min-h-screen bg-[#0c0c0c] text-zinc-100 antialiased" data-testid="gpu-landing">

      {/* ── 1. HERO ── */}
      <section className="relative px-6 pt-14 pb-10 md:pt-24 md:pb-16 max-w-3xl mx-auto text-center" data-testid="gpu-hero">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-emerald-400 mb-6"
        >
          <Cpu className="w-4 h-4" /> Location de puissance GPU
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="text-[34px] md:text-[52px] leading-[1.08] font-medium tracking-tight"
        >
          Loue de la puissance de calcul<br />
          <span className="text-emerald-400">et reçois des revenus.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-5 text-zinc-400 text-[15px] md:text-[17px] leading-relaxed max-w-xl mx-auto"
        >
          Mets de la puissance GPU à disposition et participe aux revenus générés. Simple, transparent, sans engagement.
        </motion.p>
        <motion.button
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
          onClick={() => scrollTo("gpu-calculateur")}
          data-testid="gpu-hero-cta"
          className="mt-8 inline-flex items-center gap-3 px-8 h-13 py-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[13px] tracking-[0.12em] uppercase font-semibold transition-colors"
        >
          Estimer mes revenus potentiels <ArrowRight className="w-4 h-4" />
        </motion.button>
      </section>

      {/* ── 2. FORMULAIRE ── */}
      <section id="gpu-form" className="px-6 pb-12" data-testid="gpu-form-section">
        <div className="max-w-md mx-auto rounded-2xl border border-zinc-800 bg-zinc-950/80 p-6 md:p-8">
          {!done ? (
            <form onSubmit={submit} className="space-y-4" data-testid="gpu-form">
              <h2 className="text-lg font-medium tracking-tight text-center">Être rappelé par un conseiller</h2>
              <input
                data-testid="gpu-name" required minLength={2} maxLength={120}
                value={form.full_name} onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Prénom"
                className="w-full h-12 rounded-xl bg-zinc-900 border border-zinc-800 px-4 text-[15px] focus:outline-none focus:border-emerald-500/60 transition-colors"
              />
              <input
                data-testid="gpu-email" required type="email" maxLength={200}
                value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="Email"
                className="w-full h-12 rounded-xl bg-zinc-900 border border-zinc-800 px-4 text-[15px] focus:outline-none focus:border-emerald-500/60 transition-colors"
              />
              <input
                data-testid="gpu-phone" required type="tel" minLength={6} maxLength={25}
                value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Numéro de téléphone"
                className="w-full h-12 rounded-xl bg-zinc-900 border border-zinc-800 px-4 text-[15px] focus:outline-none focus:border-emerald-500/60 transition-colors"
              />
              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <input
                  data-testid="gpu-consent" type="checkbox"
                  checked={form.consent} onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-emerald-500"
                />
                <span className="text-[12px] leading-relaxed text-zinc-400">
                  Je souhaite recevoir des informations sur la location de puissance de calcul et les revenus associés.
                </span>
              </label>
              {err && <p data-testid="gpu-error" className="text-[13px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</p>}
              <button
                data-testid="gpu-submit" disabled={busy} type="submit"
                className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[13px] tracking-[0.12em] uppercase font-semibold disabled:opacity-50 transition-colors"
              >
                {busy ? "Envoi…" : "Être rappelé"}
              </button>
              <button
                type="button" onClick={() => scrollTo("gpu-calculateur")}
                data-testid="gpu-to-calculator"
                className="w-full text-[12px] tracking-[0.1em] uppercase text-zinc-400 hover:text-emerald-400 transition-colors pt-1"
              >
                Accéder au calculateur ↓
              </button>
              <p className="text-[11px] text-zinc-500 text-center">Un conseiller vous contactera rapidement.</p>
            </form>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6" data-testid="gpu-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <Check className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="mt-5 text-xl font-medium tracking-tight">C'est noté !</h2>
              <p className="mt-3 text-zinc-400 text-[14px] leading-relaxed">
                Un conseiller vous contactera rapidement. En attendant, tu peux estimer tes revenus potentiels avec notre calculateur ci-dessous.
              </p>
              <button
                onClick={() => scrollTo("gpu-calculateur")}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors"
                data-testid="gpu-success-cta"
              >
                Accéder au calculateur <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* ── 3. COMMENT ÇA MARCHE ── */}
      <section className="px-6 py-12 border-t border-zinc-900" data-testid="gpu-steps">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.25em] uppercase text-emerald-400 text-center mb-8">Comment ça marche</p>
          <div className="space-y-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex gap-5 items-start"
                data-testid={`gpu-step-${i}`}
              >
                <span className="font-mono text-emerald-400/70 text-sm pt-1">{s.n}</span>
                <div>
                  <h3 className="text-[17px] font-medium tracking-tight">{s.t}</h3>
                  <p className="mt-1 text-[13.5px] text-zinc-400 leading-relaxed">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. BÉNÉFICES ── */}
      <section className="px-6 py-12 border-t border-zinc-900" data-testid="gpu-benefits">
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => (
            <div key={i} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5" data-testid={`gpu-benefit-${i}`}>
              <b.icon className="w-5 h-5 text-emerald-400 mb-3" />
              <h3 className="text-[15px] font-medium tracking-tight">{b.t}</h3>
              <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 5. CALCULATEUR ── */}
      <section id="gpu-calculateur" className="px-6 py-14 border-t border-zinc-900 bg-gradient-to-b from-emerald-500/5 to-transparent" data-testid="gpu-calculator">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.25em] uppercase text-emerald-400 mb-4">Calculateur</p>
          <h2 className="text-[26px] md:text-[34px] font-medium tracking-tight leading-tight">
            Estime tes revenus potentiels en 30 secondes
          </h2>
          <p className="mt-4 text-zinc-400 text-[14px] leading-relaxed max-w-md mx-auto">
            Déplace le curseur, choisis la puissance, et découvre instantanément une estimation indicative des revenus associés.
          </p>
          <GpuCalculator onCta={() => scrollTo("gpu-form")} />
          <p className="mt-4 text-[11px] text-zinc-500">Estimations indicatives — les revenus réels varient selon l'utilisation.</p>
        </div>
      </section>

      {/* ── 6. FAQ ── */}
      <section className="px-6 py-12 border-t border-zinc-900" data-testid="gpu-faq">
        <div className="max-w-2xl mx-auto">
          <p className="text-[11px] tracking-[0.25em] uppercase text-emerald-400 text-center mb-8">Questions fréquentes</p>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="group rounded-xl border border-zinc-800 bg-zinc-950/60 px-5 py-4" data-testid={`gpu-faq-${i}`}>
                <summary className="cursor-pointer text-[15px] font-medium tracking-tight list-none flex items-center justify-between">
                  {f.q}
                  <span className="text-emerald-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. DISCLAIMER ── */}
      <footer className="px-6 py-8 border-t border-zinc-900" data-testid="gpu-disclaimer">
        <div className="max-w-2xl mx-auto text-center space-y-3">
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Contenu à visée informative. Cette page ne constitue ni une offre, ni une sollicitation d'investissement, ni un conseil financier.
          </p>
          <p className="text-[10px] text-zinc-600">
            Vos données sont utilisées uniquement pour vous recontacter. Vous pouvez demander leur suppression à tout moment (RGPD).
          </p>
        </div>
      </footer>
    </div>
  );
}
